import React, { useMemo, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import virtualPosService from '../../../../services/virtualPosService';
import {
  cancelSubscription as yoolpayCancelSubscription,
  generateChangeCardUrl as yoolpayGenerateChangeCardUrl,
} from '../../../../services/yoolpay/subscriptionsApi';
import { supabase } from '../../../../services/supabaseClient.js';
import { mapChargeStatusToDb as sharedMapChargeStatusToDb, refreshChargeMetadata as refreshChargeMetadataShared } from '../../../../services/payments/virtualposChargeSync.js';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Badge } from '../../../../components/ui/badge';
import { Alert, AlertDescription } from '../../../../components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../../components/ui/dialog';
import { Label } from '../../../../components/ui/label';
import { Textarea } from '../../../../components/ui/textarea';
import { Input } from '../../../../components/ui/input';
import { ArrowLeft, RefreshCw, AlertCircle, User, CreditCard, Calendar, DollarSign, FileText, MapPin, Eye, Link2, Copy, ExternalLink, LifeBuoy, Mail, MessageCircle, Smartphone } from 'lucide-react';
import formatters from '../../../../styles/tokens/formatters';
import VirtualPosPaymentDetailDialog from '../../../../components/payments/VirtualPosPaymentDetailDialog';

const VirtualPosSubscriptionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [overdueCharges, setOverdueCharges] = useState([]);
  const [dbSubscription, setDbSubscription] = useState(null);
  const [dbCertificateCode, setDbCertificateCode] = useState(null);
  const [subscriptionCharges, setSubscriptionCharges] = useState([]);
  const [chargesLoading, setChargesLoading] = useState(false);
  const [subscriptionCertificates, setSubscriptionCertificates] = useState([]);
  const [certificatesLoading, setCertificatesLoading] = useState(false);
  const [refreshingChargeId, setRefreshingChargeId] = useState(null);

  // Recovery / changecard
  const [changecardLoading, setChangecardLoading] = useState(false);
  const [changecardUrl, setChangecardUrl] = useState(null);
  const [changecardError, setChangecardError] = useState(null);
  const [changecardActionFeedback, setChangecardActionFeedback] = useState(null);
  const [changecardEmailLoading, setChangecardEmailLoading] = useState(false);
  // Destinatario editable: la tarjeta puede ser de otra persona (quien paga).
  const [changecardEmailTo, setChangecardEmailTo] = useState('');
  const [planSuscriptionUrl, setPlanSuscriptionUrl] = useState(null);
  const [copiedFlag, setCopiedFlag] = useState(null); // 'changecard' | 'plan'

  // Cancelación de suscripción
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelConfirmText, setCancelConfirmText] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState(null);

  useEffect(() => {
    if (id) {
      loadSubscription();
    }
  }, [id]);

  const getOverdueChargesFromSubscription = (suscription) => {
    const charges = Array.isArray(suscription?.charge_program) ? suscription.charge_program : [];
    const today = new Date();
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    return charges.filter((charge) => {
      const dt = new Date(charge?.charge_date);
      if (Number.isNaN(dt.getTime())) return false;
      const status = String(charge?.status || '').toLowerCase();
      const isPaidOrCancelled = status === 'pagado' || status === 'cancelado';
      return dt < todayStart && !isPaidOrCancelled;
    });
  };

  const loadSubscription = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await virtualPosService.getSubscriptionById(id);
      setSubscription(response.suscription);
      setOverdueCharges(getOverdueChargesFromSubscription(response?.suscription));

      try {
        const { data: dbData, error: dbError } = await supabase
          .from('company_suscripciones')
          .select('id,subscription_id,codigo,company_id,compania_nombre,compania_rut,external_subscription_id,certificado_id,contratacion_id,orden_pago_id,pago_id,virtualpos_plan_id,payment_provider,es_pago_propio')
          .eq('external_subscription_id', String(id))
          .limit(1)
          .maybeSingle();

        if (dbError) {
          console.error('Error loading company_suscripciones:', dbError);
          setDbSubscription(null);
          setDbCertificateCode(null);
          setSubscriptionCharges([]);
        } else {
          setDbSubscription(dbData || null);

          // Load plan suscription_url for recovery actions
          if (dbData?.virtualpos_plan_id) {
            try {
              const plan = await virtualPosService.getPlanById(String(dbData.virtualpos_plan_id));
              const planUrl = plan?.suscription_url || plan?.plan?.suscription_url || null;
              setPlanSuscriptionUrl(planUrl);
            } catch (planErr) {
              console.warn('Could not load plan suscription_url:', planErr?.message);
            }
          }
          setDbCertificateCode(null);
          setSubscriptionCertificates([]);
          if (dbData?.id) {
            if (dbData?.certificado_id) {
              try {
                const { data: certData, error: certError } = await supabase
                  .from('certificados')
                  .select('codigo')
                  .eq('id', String(dbData.certificado_id))
                  .limit(1)
                  .maybeSingle();
                if (!certError) {
                  setDbCertificateCode(certData?.codigo || null);
                }
              } catch (certUnexpectedError) {
                console.error('Unexpected error loading certificado code:', certUnexpectedError);
              }
            }

            setChargesLoading(true);
            const subscriptionIdForCharges = dbData?.subscription_id
              ? String(dbData.subscription_id)
              : dbData?.id
                ? String(dbData.id)
                : null;
            const { data: chargesData, error: chargesError } = await supabase
              .from('company_subscription_charges')
              .select('*')
              .eq('subscription_id', subscriptionIdForCharges)
              .order('charge_date', { ascending: true });

            if (chargesError) {
              console.error('Error loading company_subscription_charges:', chargesError);
              setSubscriptionCharges([]);
            } else {
              setSubscriptionCharges(Array.isArray(chargesData) ? chargesData : []);
            }
            setChargesLoading(false);

            try {
              setCertificatesLoading(true);
              const { data: certsData, error: certsError } = await supabase
                .from('certificados')
                .select('id,fecha_emision,archivo_id,pago_id,subscription_id')
                .eq('subscription_id', dbData.id)
                .order('fecha_emision', { ascending: false });
              if (certsError) {
                console.error('Error loading certificados for subscription:', certsError);
                setSubscriptionCertificates([]);
              } else {
                setSubscriptionCertificates(Array.isArray(certsData) ? certsData : []);
              }
            } finally {
              setCertificatesLoading(false);
            }
          } else {
            setSubscriptionCharges([]);
          }
        }
    } catch (dbUnexpectedError) {
      console.error('Unexpected error loading subscription charges:', dbUnexpectedError);
      setDbSubscription(null);
      setDbCertificateCode(null);
      setSubscriptionCharges([]);
    } finally {
      setChargesLoading(false);
    }
    } catch (err) {
      setError('Error al cargar la suscripción');
      console.error('Error loading subscription:', err);
    } finally {
      setLoading(false);
    }
  };

  const syncSubscription = async () => {
    try {
      setSyncing(true);
      setError(null);
      const response = await virtualPosService.syncSubscription(id);
      setSubscription(response?.suscription);
      const overdueFromResponse = Array.isArray(response?.overdue_charges) ? response.overdue_charges : null;
      setOverdueCharges(overdueFromResponse || getOverdueChargesFromSubscription(response?.suscription));
    } catch (err) {
      setError('Error al sincronizar la suscripción');
      console.error('Error syncing subscription:', err);
    } finally {
      setSyncing(false);
    }
  };

  const getStatusBadge = (status) => {
    const STATUS_MAP = {
      'ACTIVA':              { variant: 'pagado',     label: 'Activa' },
      'CANCELADA':           { variant: 'cancelado',  label: 'Cancelada' },
      'SUSCRIBIENDO':        { variant: 'procesando', label: 'Suscribiendo' },
      'SUSCRIPCION_FALLIDA': { variant: 'expirado',   label: 'Suscripción Fallida' },
      'FINALIZADA':          { variant: 'cancelado',  label: 'Finalizada' },
      'EXPIRADA':            { variant: 'expirado',   label: 'Expirada' },
    };
    const cfg = STATUS_MAP[status] || { variant: 'outline', label: status || '—' };
    return <Badge variant={cfg.variant}>{cfg.label}</Badge>;
  };

  const getChargeStatusBadge = (status) => {
    const raw = String(status || '').toLowerCase();
    const CHARGE_MAP = {
      'pendiente':  { variant: 'pendiente',  label: 'Pendiente' },
      'pagado':     { variant: 'pagado',     label: 'Pagado' },
      'cancelado':  { variant: 'cancelado',  label: 'Cancelado' },
      'procesando': { variant: 'procesando', label: 'Procesando' },
      'rechazado':  { variant: 'expirado',   label: 'Rechazado' },
      'failed':     { variant: 'expirado',   label: 'Fallido' },
    };
    const cfg = CHARGE_MAP[raw] || { variant: 'outline', label: status || '—' };
    return <Badge variant={cfg.variant}>{cfg.label}</Badge>;
  };

  const formatCurrency = (amount, currency = 'CLP') => {
    if (currency === 'UF') {
      return `${amount} UF`;
    }
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: currency === 'UF' ? 'CLP' : currency
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-CL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateDDMMYYYY = (dateString) => {
    if (!dateString) return '-';
    const value = String(dateString);
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
      const [, yyyy, mm, dd] = match;
      return `${dd}-${mm}-${yyyy}`;
    }
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return '-';
    const dd = String(dt.getDate()).padStart(2, '0');
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const yyyy = String(dt.getFullYear());
    return `${dd}-${mm}-${yyyy}`;
  };

  const getMetadataObject = (metadataValue) => {
    if (!metadataValue) return null;
    if (typeof metadataValue === 'string') {
      try {
        return JSON.parse(metadataValue);
      } catch (_) {
        return null;
      }
    }
    if (typeof metadataValue === 'object') return metadataValue;
    return null;
  };

  const formatCLP = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const numericValue = typeof value === 'string' ? Number(value) : value;
    if (typeof numericValue !== 'number' || Number.isNaN(numericValue)) return '-';
    return formatters.formatCLP(numericValue);
  };

  const parseNumber = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value !== 'string') return null;
    const trimmed = value.replace(/\s/g, '');
    const hasDot = trimmed.includes('.');
    const hasComma = trimmed.includes(',');
    const normalized = (() => {
      if (hasDot && hasComma) {
        return trimmed.replace(/\./g, '').replace(/,/g, '.');
      }
      if (hasComma && !hasDot) {
        return trimmed.replace(/,/g, '.');
      }
      return trimmed;
    })();
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const refreshChargeMetadata = async (row) => {
    const chargeId = row?.external_charge_id ? String(row.external_charge_id) : '';
    const dbChargeId = row?.dbRow?.id ? String(row.dbRow.id) : '';
    if (!chargeId || !dbChargeId) return;

    try {
      setRefreshingChargeId(chargeId);
      const { metadata: newMetadata, derivedStatus, updatePayload } = await refreshChargeMetadataShared({
        getChargeById: virtualPosService.getChargeById.bind(virtualPosService),
        getPaymentById: virtualPosService.getPaymentById.bind(virtualPosService),
        supabase,
        dbChargeId,
        externalChargeId: chargeId,
        existingMetadata: row?.dbRow?.payment_metadata || null,
        // Para no pisar un `processed_at` ya registrado al promover el cobro.
        existingCharge: row?.dbRow || null,
      });
      if (!newMetadata) return;

      setSubscriptionCharges((prev) => {
        if (!Array.isArray(prev)) return prev;
        return prev.map((item) => {
          if (String(item?.id || '') !== dbChargeId) return item;
          return {
            ...item,
            payment_metadata: newMetadata,
            ...(derivedStatus ? { status: derivedStatus } : {}),
            // Las columnas que el refresco promueve desde la metadata: sin esto
            // la fila seguía en $0 hasta recargar.
            ...(updatePayload?.amount_paid !== undefined
              ? { amount_paid: updatePayload.amount_paid }
              : {}),
            ...(updatePayload?.processed_at
              ? { processed_at: updatePayload.processed_at }
              : {})
          };
        });
      });
    } catch (e) {
      console.error('Error refreshing charge metadata:', e);
    } finally {
      setRefreshingChargeId(null);
    }
  };

  const handleGenerateChangecard = async () => {
    if (!subscription?.id) return;
    setChangecardLoading(true);
    setChangecardError(null);
    setChangecardActionFeedback(null);
    setChangecardUrl(null);
    try {
      const token = await getToken();
      const result = await yoolpayGenerateChangeCardUrl(token, String(subscription.id));
      const url = result?.url || null;
      if (url) {
        setChangecardUrl(url);
        setChangecardEmailTo(String(subscription?.client?.email || '').trim());
      } else {
        setChangecardError('VirtualPos no devolvió una URL. Es posible que la suscripción ya no esté activa en VirtualPos.');
      }
    } catch (err) {
      setChangecardError(err?.message || 'Error al generar el link de cambio de tarjeta.');
    } finally {
      setChangecardLoading(false);
    }
  };

  const handleCopyUrl = (url, key) => {
    if (!url) return;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedFlag(key);
      setTimeout(() => setCopiedFlag(null), 2000);
    });
  };

  const getChangecardMessage = (url) => {
    const planName = subscription?.plan_name || 'tu suscripción';
    return `Hola, puedes actualizar tu medio de pago para ${planName} en este enlace: ${url}`;
  };

  const getClientPhoneDigits = () => String(subscription?.client?.phone || '').replace(/\D/g, '');

  const closeCancelDialog = () => {
    setShowCancelDialog(false);
    setCancelReason('');
    setCancelConfirmText('');
    setCancelError(null);
  };

  const handleCancelSubscription = async () => {
    if (cancelConfirmText !== 'CANCELAR' || !subscription) return;
    setCancelling(true);
    setCancelError(null);
    try {
      const token = await getToken();
      await yoolpayCancelSubscription(token, subscription.id, cancelReason);
      closeCancelDialog();
      loadSubscription();
    } catch (err) {
      console.error('Error cancelling subscription:', err);
      setCancelError(err?.message || 'Error al cancelar la suscripción');
    } finally {
      setCancelling(false);
    }
  };

  const handleSendChangecardEmail = async () => {
    if (!changecardUrl) return;

    const clientEmail = String(subscription?.client?.email || '').trim();
    const recipientEmail = String(changecardEmailTo || clientEmail).trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail)) {
      setChangecardActionFeedback({ type: 'error', text: 'Ingresa un email válido para enviar el link.' });
      return;
    }
    // Si va a otra persona, no saludarla con el nombre del titular.
    const isClient = recipientEmail.toLowerCase() === clientEmail.toLowerCase();

    try {
      setChangecardEmailLoading(true);
      setChangecardActionFeedback(null);
      const token = await getToken();
      await virtualPosService.sendChangeCardLinkEmail(String(subscription?.id), {
        email: recipientEmail,
        url: changecardUrl,
        customerName: isClient
          ? `${subscription?.client?.first_name || ''} ${subscription?.client?.last_name || ''}`.trim() || 'Cliente'
          : 'Cliente',
        planName: subscription?.plan_name || 'tu plan',
        productName: subscription?.channel || 'tu producto',
      }, token);
      setChangecardActionFeedback({ type: 'success', text: `Link enviado por email a ${recipientEmail}.` });
    } catch (err) {
      setChangecardActionFeedback({ type: 'error', text: err?.message || 'No se pudo enviar el link por email.' });
    } finally {
      setChangecardEmailLoading(false);
    }
  };

  const handleShareChangecardWhatsApp = () => {
    if (!changecardUrl) return;

    const phone = getClientPhoneDigits();
    if (!phone) {
      setChangecardActionFeedback({ type: 'error', text: 'La suscripción no tiene teléfono para compartir por WhatsApp.' });
      return;
    }

    const text = encodeURIComponent(getChangecardMessage(changecardUrl));
    window.open(`https://wa.me/${phone}?text=${text}`, '_blank', 'noopener,noreferrer');
  };

  const handleShareChangecardSms = () => {
    if (!changecardUrl) return;

    const phone = getClientPhoneDigits();
    if (!phone) {
      setChangecardActionFeedback({ type: 'error', text: 'La suscripción no tiene teléfono para compartir por SMS.' });
      return;
    }

    const text = encodeURIComponent(getChangecardMessage(changecardUrl));
    window.open(`sms:${phone}?body=${text}`, '_self');
  };

  const normalizeChargeStatus = (statusValue) => {
    const status = String(statusValue || '').toLowerCase();
    const map = {
      paid: 'pagado',
      pending: 'pendiente',
      cancelled: 'cancelado',
      canceled: 'cancelado',
      processing: 'procesando',
      rejected: 'rechazado',
      failed: 'rechazado',
    };
    return map[status] || status;
  };

  const mapChargeStatusToDb = (statusValue) => {
    return sharedMapChargeStatusToDb(statusValue);
  };

  const getPaymentTrackingFromMetadata = (metadata) => {
    const chargedCLP =
      metadata?.charged_amount_clp ??
      metadata?.amount_clp ??
      metadata?.charged_clp ??
      metadata?.amount_pesos ??
      metadata?.clp_amount ??
      metadata?.payment?.amount_clp ??
      metadata?.payment?.charged_amount_clp ??
      metadata?.virtualpos_payment?.amount_clp ??
      null;

    const netCLP =
      metadata?.net_amount_clp ??
      metadata?.net_clp ??
      metadata?.recaudado_clp ??
      metadata?.payment?.net_amount_clp ??
      metadata?.virtualpos_payment?.net_amount_clp ??
      null;

    const feesCLP =
      metadata?.fees_clp ??
      metadata?.fee_clp ??
      metadata?.commissions_clp ??
      metadata?.commission_clp ??
      metadata?.payment?.fees_clp ??
      metadata?.payment?.commission_clp ??
      metadata?.virtualpos_payment?.fees_clp ??
      null;

    const depositDate =
      metadata?.deposit_date ??
      metadata?.deposit_at ??
      metadata?.deposited_at ??
      metadata?.settlement_date ??
      metadata?.settled_at ??
      metadata?.payment?.deposit_date ??
      metadata?.payment?.settlement_date ??
      metadata?.virtualpos_payment?.deposit_date ??
      null;

    const paidAt =
      metadata?.paid_at ??
      metadata?.transaction_date ??
      metadata?.processed_at ??
      metadata?.payment?.paid_at ??
      metadata?.virtualpos_payment?.paid_at ??
      null;

    const order = metadata?.charge?.payment?.order || null;
    const deposits = Array.isArray(order?.deposits) ? order.deposits : [];
    const firstDeposit = deposits?.[0] || null;

    const chargedFromOrder = parseNumber(order?.amount);
    const netFromDeposit = parseNumber(firstDeposit?.payout_amount);
    const feesFromDeposit = (() => {
      const processingFee = parseNumber(firstDeposit?.processing_fee);
      const flatFee = parseNumber(firstDeposit?.processing_flat_fee);
      if (processingFee === null && flatFee === null) return null;
      return (processingFee || 0) + (flatFee || 0);
    })();

    return {
      chargedCLP: chargedCLP ?? chargedFromOrder,
      netCLP: netCLP ?? netFromDeposit,
      feesCLP: feesCLP ?? feesFromDeposit,
      depositDate: depositDate ?? firstDeposit?.payout_date ?? null,
      paidAt: paidAt ?? order?.authorized_at ?? order?.created_at ?? null
    };
  };

  const unifiedCharges = useMemo(() => {
    const apiCharges = Array.isArray(subscription?.charge_program) ? subscription.charge_program : [];
    const dbCharges = Array.isArray(subscriptionCharges) ? subscriptionCharges : [];
    const dbByExternalId = new Map(
      dbCharges
        .filter((row) => row?.external_charge_id)
        .map((row) => [String(row.external_charge_id), row])
    );

    const usedDbIds = new Set();
    const merged = apiCharges.map((apiCharge) => {
      const externalChargeId = String(apiCharge?.id || '');
      const dbRow = externalChargeId ? dbByExternalId.get(externalChargeId) : null;
      if (dbRow?.id) usedDbIds.add(String(dbRow.id));
      const metadata = getMetadataObject(dbRow?.payment_metadata || dbRow?.provider_metadata);
      const tracking = getPaymentTrackingFromMetadata(metadata);
      return {
        key: externalChargeId || String(dbRow?.id || ''),
        apiCharge: apiCharge || null,
        dbRow: dbRow || null,
        external_charge_id: externalChargeId || dbRow?.external_charge_id || null,
        charge_date: dbRow?.charge_date || apiCharge?.charge_date || null,
        description: dbRow?.description || apiCharge?.description || null,
        amount: dbRow?.amount ?? apiCharge?.amount ?? null,
        currency: dbRow?.currency || subscription?.currency || null,
        status: normalizeChargeStatus(dbRow?.status || apiCharge?.status),
        processed_at: dbRow?.processed_at || null,
        tracking,
        metadata
      };
    });

    dbCharges.forEach((dbRow) => {
      if (!dbRow?.id) return;
      if (usedDbIds.has(String(dbRow.id))) return;
      const metadata = getMetadataObject(dbRow?.payment_metadata || dbRow?.provider_metadata);
      const tracking = getPaymentTrackingFromMetadata(metadata);
      merged.push({
        key: String(dbRow.id),
        apiCharge: null,
        dbRow,
        external_charge_id: dbRow?.external_charge_id || null,
        charge_date: dbRow?.charge_date || null,
        description: dbRow?.description || null,
        amount: dbRow?.amount ?? null,
        currency: dbRow?.currency || null,
        status: normalizeChargeStatus(dbRow?.status),
        processed_at: dbRow?.processed_at || null,
        tracking,
        metadata
      });
    });

    return merged.sort((a, b) => {
      const aDate = a?.charge_date ? new Date(a.charge_date).getTime() : 0;
      const bDate = b?.charge_date ? new Date(b.charge_date).getTime() : 0;
      return aDate - bDate;
    });
  }, [subscription, subscriptionCharges]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="h-8 w-8 animate-spin" />
        <span className="ml-2">Cargando suscripción...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => navigate('/payments-admin/virtualpos/subscriptions')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver
          </Button>
          <h1 className="text-2xl font-bold">Detalle de Suscripción</h1>
        </div>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => navigate('/payments-admin/virtualpos/subscriptions')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver
          </Button>
          <h1 className="text-2xl font-bold">Suscripción no encontrada</h1>
        </div>
      </div>
    );
  }

  // Changecard: sirve para una suscripción ACTIVA (el cliente cambia su tarjeta)
  // y para recuperar una SUSCRIBIENDO / SUSCRIPCION_FALLIDA.
  const changecardSection = (
    <div className="space-y-2">
      <div>
        <p className="text-sm font-medium">Link de cambio de tarjeta (Changecard)</p>
        <p className="text-xs text-muted-foreground mt-0.5">
          {subscription.status === 'ACTIVA'
            ? 'Permite al cliente registrar un nuevo medio de pago. Los próximos cargos se hacen a la tarjeta nueva.'
            : subscription.status === 'SUSCRIBIENDO'
                ? 'Permite al cliente registrar un nuevo medio de pago. Funciona mientras VirtualPos tenga la suscripción activa (ventana ≈ 12h).'
              : 'La suscripción está en estado fallida en VirtualPos — el link de cambio de tarjeta probablemente no sea válido, pero puedes intentarlo.'}
        </p>
      </div>

      {changecardError && (
        <Alert variant="destructive" className="py-2">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-xs">{changecardError}</AlertDescription>
        </Alert>
      )}

      {changecardActionFeedback && (
        <Alert variant={changecardActionFeedback.type === 'error' ? 'destructive' : 'default'} className="py-2">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-xs">{changecardActionFeedback.text}</AlertDescription>
        </Alert>
      )}

      {changecardUrl ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2 p-2 bg-muted rounded-lg text-xs font-mono break-all">
            <span className="flex-1">{changecardUrl}</span>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 shrink-0"
              onClick={() => handleCopyUrl(changecardUrl, 'changecard')}
              title="Copiar URL"
            >
              {copiedFlag === 'changecard' ? '✓' : <Copy className="h-3.5 w-3.5" />}
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 shrink-0"
              onClick={() => window.open(changecardUrl, '_blank')}
              title="Abrir en nueva pestaña"
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </Button>
          </div>
          <div className="space-y-1">
            <label htmlFor="changecard-email-to" className="text-xs font-medium text-gray-600">
              Enviar a
            </label>
            <Input
              id="changecard-email-to"
              type="email"
              value={changecardEmailTo}
              placeholder={subscription?.client?.email || 'email@ejemplo.cl'}
              onChange={(e) => setChangecardEmailTo(e.target.value)}
              className="h-8 text-sm"
            />
            <p className="text-xs text-muted-foreground">
              Por defecto, el email del cliente. Cámbialo si la tarjeta es de otra persona.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleSendChangecardEmail}
              disabled={changecardEmailLoading || !(changecardEmailTo || subscription?.client?.email)}
            >
              {changecardEmailLoading ? (
                <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Enviando...</>
              ) : (
                <><Mail className="h-4 w-4 mr-2" />Enviar por email</>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleShareChangecardWhatsApp}
              disabled={!subscription?.client?.phone}
            >
              <MessageCircle className="h-4 w-4 mr-2" />
              WhatsApp
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleShareChangecardSms}
              disabled={!subscription?.client?.phone}
            >
              <Smartphone className="h-4 w-4 mr-2" />
              SMS
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Usa el email del cliente o su teléfono registrado para compartir este link.
          </p>
        </div>
      ) : (
        <Button
          variant="outline"
          size="sm"
          onClick={handleGenerateChangecard}
          disabled={changecardLoading}
        >
          {changecardLoading ? (
            <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Generando...</>
          ) : (
            <><Link2 className="h-4 w-4 mr-2" />Generar link changecard</>
          )}
        </Button>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => navigate('/payments-admin/virtualpos/subscriptions')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Suscripción #{subscription.id}</h1>
            <p className="text-gray-600">{subscription.plan_name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {getStatusBadge(subscription.status)}
          <Button variant="outline" size="sm" onClick={loadSubscription}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualizar
          </Button>
          <Button variant="outline" size="sm" onClick={syncSubscription} disabled={syncing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            Sync Estado
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Información del Cliente */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Información del Cliente
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <span className="font-medium text-gray-600">Nombre:</span>
              <p>{subscription.client?.first_name} {subscription.client?.last_name}</p>
            </div>
            <div>
              <span className="font-medium text-gray-600">Email:</span>
              <p>{subscription.client?.email}</p>
            </div>
            {subscription.client?.phone && (
              <div>
                <span className="font-medium text-gray-600">Teléfono:</span>
                <p>{subscription.client.phone}</p>
              </div>
            )}
            {subscription.client?.social_id && (
              <div>
                <span className="font-medium text-gray-600">RUT/ID:</span>
                <p>{subscription.client.social_id}</p>
              </div>
            )}
            {subscription.client?.birth_date && (
              <div>
                <span className="font-medium text-gray-600">Fecha de Nacimiento:</span>
                <p>{formatDate(subscription.client.birth_date)}</p>
              </div>
            )}
            {subscription.client?.gender && (
              <div>
                <span className="font-medium text-gray-600">Género:</span>
                <p>{subscription.client.gender}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Información de Pago */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Información de Pago
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {(dbSubscription?.payment_provider || dbSubscription?.es_pago_propio != null) && (
              <div>
                <span className="font-medium text-gray-600">PSP de origen:</span>
                <p className="flex items-center gap-2">
                  <span className="font-mono">{dbSubscription?.payment_provider || '—'}</span>
                  {dbSubscription?.es_pago_propio === true && (
                    <Badge variant="secondary" className="text-xs">Pago propio de la compañía</Badge>
                  )}
                  {dbSubscription?.es_pago_propio === false && (
                    <Badge variant="outline" className="text-xs">PSP de Yool</Badge>
                  )}
                </p>
              </div>
            )}
            <div>
              <span className="font-medium text-gray-600">Plan:</span>
              <p>{subscription.plan_name}</p>
            </div>
            <div>
              <span className="font-medium text-gray-600">Monto:</span>
              <p className="text-lg font-semibold">
                {formatCurrency(subscription.amount, subscription.currency)}
              </p>
            </div>
            {subscription.payment_method && (
              <div>
                <span className="font-medium text-gray-600">Método de Pago:</span>
                <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="font-medium">Marca:</span>
                      <p>{subscription.payment_method.brand || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="font-medium">Últimos 4 dígitos:</span>
                      <p>****{subscription.payment_method.last4CardDigit || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="font-medium">Emisor:</span>
                      <p>{subscription.payment_method.issuer || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="font-medium">Vencimiento:</span>
                      <p>{subscription.payment_method.expiration_date || 'N/A'}</p>
                    </div>
                    {subscription.payment_method.bin && (
                      <div>
                        <span className="font-medium">BIN:</span>
                        <p>{subscription.payment_method.bin}</p>
                      </div>
                    )}
                    <div>
                      <span className="font-medium">Almacenamiento:</span>
                      <p>{subscription.payment_method.card_on_file || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div>
              <span className="font-medium text-gray-600">Renovación Automática:</span>
              <p>{subscription.automatic_renewal === 'T' ? 'Sí' : 'No'}</p>
            </div>
            {subscription.service_id && (
              <div>
                <span className="font-medium text-gray-600">Service ID:</span>
                <p className="font-mono text-sm">{subscription.service_id}</p>
              </div>
            )}
            {subscription.channel && (
              <div>
                <span className="font-medium text-gray-600">Canal:</span>
                <p>{subscription.channel}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Fechas Importantes */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Fechas Importantes
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <span className="font-medium text-gray-600">Fecha de Suscripción:</span>
              <p>{formatDate(subscription.suscription_date)}</p>
            </div>
            {subscription.next_payment_date && (
              <div>
                <span className="font-medium text-gray-600">Próximo Pago:</span>
                <p>{formatDate(subscription.next_payment_date)}</p>
              </div>
            )}
            {subscription.cancelled_date && (
              <div>
                <span className="font-medium text-gray-600">Fecha de Cancelación:</span>
                <p>{formatDate(subscription.cancelled_date)}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Información Adicional */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Información Adicional
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <span className="font-medium text-gray-600">Estado:</span>
              <div className="mt-1">
                {getStatusBadge(subscription.status)}
              </div>
            </div>
            {subscription.origin && (
              <div>
                <span className="font-medium text-gray-600">Origen:</span>
                <p>{subscription.origin}</p>
              </div>
            )}
            <div>
              <span className="font-medium text-gray-600">ID de Suscripción:</span>
              <p className="font-mono text-sm">{subscription.id}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cargos */}
      {((Array.isArray(subscription?.charge_program) && subscription.charge_program.length > 0) || chargesLoading || unifiedCharges.length > 0) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Cargos
            </CardTitle>
          </CardHeader>
          <CardContent>
            {overdueCharges.length > 0 && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Hay {overdueCharges.length} cargo(s) vencido(s) pendiente(s) de pago.
                </AlertDescription>
              </Alert>
            )}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-medium">Cargos (Unificado)</h3>
                {dbSubscription?.compania_nombre && (
                  <div className="text-sm text-gray-600">
                    {dbSubscription.compania_nombre} {dbSubscription.compania_rut ? `(${dbSubscription.compania_rut})` : ''}
                  </div>
                )}
              </div>

              {chargesLoading ? (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  <span>Cargando cargos...</span>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2 font-medium">Fecha</th>
                        <th className="text-left p-2 font-medium">Plan</th>
                        <th className="text-left p-2 font-medium">Monto</th>
                        <th className="text-left p-2 font-medium">Estado</th>
                        <th className="text-left p-2 font-medium">Cobrado (CLP)</th>
                        <th className="text-left p-2 font-medium">Recaudado (CLP)</th>
                        <th className="text-left p-2 font-medium">Comisiones/Fees</th>
                        <th className="text-left p-2 font-medium">Depósito</th>
                        <th className="text-left p-2 font-medium">Procesado</th>
                        <th className="text-left p-2 font-medium">Detalle</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unifiedCharges.map((row) => {
                        const metadataText = row?.metadata ? JSON.stringify(row.metadata, null, 2) : '';
                        const virtualposDeposits = row?.metadata?.charge?.payment?.order?.deposits;
                        const hasVirtualposDeposits = Array.isArray(virtualposDeposits) && virtualposDeposits.length > 0;
                        const hasCompletePaymentMetadata = !!row?.metadata?.charge?.payment?.order && hasVirtualposDeposits;
                        const isPaidCharge = String(row?.status || '').toLowerCase() === 'pagado';
                        const shouldShowRefreshButton = !!(row?.external_charge_id && row?.dbRow?.id) && !(isPaidCharge && hasCompletePaymentMetadata);
                        const virtualposCharge = row?.metadata?.charge || null;
                        const virtualposOrder = virtualposCharge?.payment?.order || null;
                        const virtualposDepositsList = Array.isArray(virtualposOrder?.deposits) ? virtualposOrder.deposits : [];
                        const chargeAmount = parseNumber(virtualposCharge?.amount);
                        const orderAmount = parseNumber(virtualposOrder?.amount);
                        const formattedDeposits = virtualposDepositsList.map((deposit) => ({
                          description: deposit?.description || '-',
                          payout_date: deposit?.payout_date ? formatDate(deposit.payout_date) : '-',
                          payout_amount: formatCLP(parseNumber(deposit?.payout_amount) ?? deposit?.payout_amount),
                          processing_fee: formatCLP(parseNumber(deposit?.processing_fee) ?? deposit?.processing_fee),
                          processing_flat_fee: formatCLP(parseNumber(deposit?.processing_flat_fee) ?? deposit?.processing_flat_fee),
                        }));

                        const identifiers = (virtualposOrder?.auth_code || virtualposOrder?.payment_type_code || virtualposOrder?.uuid || virtualposCharge?.internal_code)
                          ? [
                            { label: 'Auth code:', value: virtualposOrder?.auth_code, mono: true },
                            { label: 'Tipo de pago:', value: virtualposOrder?.payment_type_code, mono: true },
                            { label: 'Order UUID:', value: virtualposOrder?.uuid, mono: true },
                            { label: 'Internal code:', value: virtualposCharge?.internal_code, mono: true },
                          ]
                          : undefined;

                        const collectionItems = (row?.tracking?.chargedCLP || row?.tracking?.netCLP)
                          ? [
                            { label: 'Monto Cobrado (CLP):', value: formatCLP(row?.tracking?.chargedCLP || 0) },
                            { label: 'Comisión Pasarela:', value: `-${formatCLP(row?.tracking?.feesCLP || 0)}`, className: 'font-medium text-[var(--yool-error)]' },
                            { label: 'Neto Recaudado:', value: formatCLP(row?.tracking?.netCLP || 0), boldLabel: true, className: 'font-bold text-green-700' },
                            {
                              label: 'Depósito:',
                              value: row?.metadata?.charge?.payment?.order?.deposits?.[0]?.payout_date
                                ? formatDateDDMMYYYY(row.metadata.charge.payment.order.deposits[0].payout_date)
                                : row?.tracking?.depositDate
                                  ? formatDateDDMMYYYY(row.tracking.depositDate)
                                  : '-',
                            },
                            { label: 'Procesado:', value: row?.processed_at ? formatDate(row.processed_at) : row?.tracking?.paidAt ? formatDate(row.tracking.paidAt) : '-' },
                          ]
                          : undefined;

                        const chargeItems = virtualposCharge
                          ? [
                            { label: 'Charge ID:', value: virtualposCharge?.id || '-', mono: true },
                            { label: 'Estado Charge:', value: getChargeStatusBadge(virtualposCharge?.status || '-') },
                            {
                              label: 'Monto Charge:',
                              value: formatCurrency(chargeAmount ?? row?.amount, row?.currency || subscription?.currency),
                            },
                            { label: 'Fecha Charge:', value: virtualposCharge?.charge_date ? formatDate(virtualposCharge.charge_date) : '-' },
                            { label: 'Descripción:', value: virtualposCharge?.description || '-', className: 'break-words' },
                          ]
                          : undefined;

                        const orderItems = virtualposOrder
                          ? [
                            { label: 'Orden UUID:', value: virtualposOrder?.uuid || '-', mono: true },
                            { label: 'Estado Orden:', value: getChargeStatusBadge(virtualposOrder?.status || '-') },
                            { label: 'Monto Orden (CLP):', value: orderAmount !== null ? formatCLP(orderAmount) : virtualposOrder?.amount ?? '-' },
                            { label: 'Authorized At:', value: virtualposOrder?.authorized_at ? formatDate(virtualposOrder.authorized_at) : '-' },
                            { label: 'Created At:', value: virtualposOrder?.created_at ? formatDate(virtualposOrder.created_at) : '-' },
                            { label: 'Installment Number:', value: virtualposOrder?.installment_number ?? '-', mono: true },
                            { label: 'Installment Amount:', value: virtualposOrder?.installment_amount ?? '-', mono: true },
                            { label: 'Card Number:', value: virtualposOrder?.card_number ?? '-', mono: true },
                          ]
                          : undefined;

                        return (
                          <tr key={row?.key} className="border-b">
                            <td className="p-2">{row?.charge_date ? formatDate(row.charge_date) : '-'}</td>
                            <td className="p-2">{subscription?.plan_name || '-'}</td>
                            <td className="p-2">{formatCurrency(row?.amount, row?.currency || subscription?.currency)}</td>
                            <td className="p-2">{getChargeStatusBadge(row?.status)}</td>
                            <td className="p-2">{formatCLP(row?.tracking?.chargedCLP)}</td>
                            <td className="p-2">{formatCLP(row?.tracking?.netCLP)}</td>
                            <td className="p-2">{formatCLP(row?.tracking?.feesCLP)}</td>
                            <td className="p-2">
                              {row?.metadata?.charge?.payment?.order?.deposits?.[0]?.payout_date
                                ? formatDateDDMMYYYY(row.metadata.charge.payment.order.deposits[0].payout_date)
                                : row?.tracking?.depositDate
                                  ? formatDateDDMMYYYY(row.tracking.depositDate)
                                  : '-'}
                            </td>
                            <td className="p-2">{row?.processed_at ? formatDate(row.processed_at) : row?.tracking?.paidAt ? formatDate(row.tracking.paidAt) : '-'}</td>
                            <td className="p-2">
                              <VirtualPosPaymentDetailDialog
                                trigger={(
                                  <Button variant="ghost" size="sm">
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                )}
                                headerRight={shouldShowRefreshButton ? (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => refreshChargeMetadata(row)}
                                    disabled={refreshingChargeId === String(row.external_charge_id)}
                                  >
                                    {refreshingChargeId === String(row.external_charge_id) ? 'Actualizando...' : 'Actualizar metadata'}
                                  </Button>
                                ) : null}
                                summary={{
                                  orderId: virtualposOrder?.uuid || '-',
                                  amount: formatCurrency(row?.amount, row?.currency || subscription?.currency),
                                  status: getChargeStatusBadge(row?.status),
                                  date: row?.charge_date ? formatDate(row.charge_date) : '-',
                                }}
                                identifiers={identifiers}
                                customerItems={[
                                  {
                                    label: 'Cliente:',
                                    value: row?.metadata?.charge?.payment?.client
                                      ? `${row?.metadata?.charge?.payment?.client?.first_name || ''} ${row?.metadata?.charge?.payment?.client?.last_name || ''}`.trim() || '-'
                                      : subscription?.client
                                        ? `${subscription?.client?.first_name || ''} ${subscription?.client?.last_name || ''}`.trim() || '-'
                                        : '-',
                                  },
                                  { label: 'Comercio (VirtualPOS):', value: row?.metadata?.charge?.payment?.commerce?.name || '-' },
                                  { label: 'Certificado:', value: dbCertificateCode || '-', mono: true },
                                  { label: 'Suscripción (BD):', value: dbSubscription?.codigo || '-', mono: true },
                                  { label: 'Suscripción (VirtualPOS):', value: subscription?.id || '-', mono: true },
                                  { label: 'External Charge ID:', value: row?.external_charge_id || '-', mono: true },
                                ]}
                                collectionItems={collectionItems}
                                chargeItems={chargeItems}
                                orderItems={orderItems}
                                deposits={formattedDeposits}
                                metadataText={metadataText}
                                extraContent={certificatesLoading ? (
                                  <div className="text-sm text-gray-600">Cargando certificados...</div>
                                ) : subscriptionCertificates.length > 0 ? (
                                  <Card>
                                    <CardHeader className="pb-2">
                                      <CardTitle className="text-sm font-medium text-gray-600">Certificados asociados a la suscripción</CardTitle>
                                    </CardHeader>
                                    <CardContent className="text-sm">
                                      <div className="space-y-1">
                                        {subscriptionCertificates.slice(0, 5).map((c) => (
                                          <div key={c?.id} className="flex items-center justify-between gap-3">
                                            <div className="font-mono break-all">{c?.id || '-'}</div>
                                            <div className="text-gray-600">{c?.fecha_emision ? formatDate(c.fecha_emision) : '-'}</div>
                                          </div>
                                        ))}
                                      </div>
                                    </CardContent>
                                  </Card>
                                ) : null}
                              />
                            </td>
                          </tr>
                        );
                      })}
                      {unifiedCharges.length === 0 && (
                        <tr>
                          <td className="p-2 text-sm text-gray-600" colSpan={10}>
                            Sin cargos registrados para esta suscripción.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Información de Dirección del Cliente */}
      {subscription.client?.address && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Dirección del Cliente
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <span className="font-medium text-gray-600">Dirección:</span>
              <p>{subscription.client.address.address_name}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <span className="font-medium text-gray-600">Calle:</span>
                <p>{subscription.client.address.street_name} {subscription.client.address.street_number}</p>
              </div>
              {subscription.client.address.dpto_number && (
                <div>
                  <span className="font-medium text-gray-600">Departamento:</span>
                  <p>{subscription.client.address.dpto_number}</p>
                </div>
              )}
              <div>
                <span className="font-medium text-gray-600">Comuna:</span>
                <p>{subscription.client.address.comuna_name}</p>
              </div>
              <div>
                <span className="font-medium text-gray-600">Región:</span>
                <p>{subscription.client.address.region_name}</p>
              </div>
            </div>
            {subscription.client.address.additional_information && (
              <div>
                <span className="font-medium text-gray-600">Información Adicional:</span>
                <p>{subscription.client.address.additional_information}</p>
              </div>
            )}
            {subscription.client.address.additional_commerce_notes && (
              <div>
                <span className="font-medium text-gray-600">Notas del Comercio:</span>
                <p>{subscription.client.address.additional_commerce_notes}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Recuperación de suscripción zombie / fallida */}
      {['SUSCRIBIENDO', 'SUSCRIPCION_FALLIDA'].includes(subscription.status) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <LifeBuoy className="h-5 w-5 text-amber-600" />
              Recuperación
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {changecardSection}

            {/* Plan suscription_url — new contratacion link */}
            {planSuscriptionUrl && (
              <div className="space-y-2 pt-2 border-t">
                <div>
                  <p className="text-sm font-medium">URL de nueva contratación</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Link del plan en el sitio web. Inicia una suscripción <strong>nueva desde cero</strong> en VirtualPos (distinto al changecard).
                  </p>
                </div>
                <div className="flex items-center gap-2 p-2 bg-muted rounded-lg text-xs font-mono break-all">
                  <span className="flex-1">{planSuscriptionUrl}</span>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 shrink-0"
                    onClick={() => handleCopyUrl(planSuscriptionUrl, 'plan')}
                    title="Copiar URL"
                  >
                    {copiedFlag === 'plan' ? '✓' : <Copy className="h-3.5 w-3.5" />}
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 shrink-0"
                    onClick={() => window.open(planSuscriptionUrl, '_blank')}
                    title="Abrir en nueva pestaña"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Acciones */}
      {subscription.status === 'ACTIVA' && (
        <Card>
          <CardHeader>
            <CardTitle>Acciones</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {changecardSection}
            <div className="border-t" />
            <div className="flex gap-4">
              <Button
                variant="destructive"
                onClick={() => setShowCancelDialog(true)}
              >
                Cancelar Suscripción
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dialog para cancelar suscripción */}
      <Dialog open={showCancelDialog} onOpenChange={(open) => { if (!open) closeCancelDialog(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancelar Suscripción</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p>¿Estás seguro de que deseas cancelar esta suscripción? Esta acción no se puede deshacer.</p>
            {subscription && (
              <div className="bg-gray-50 p-3 rounded">
                <p><strong>Plan:</strong> {subscription.plan_name}</p>
                <p><strong>Cliente:</strong> {subscription.client?.email}</p>
              </div>
            )}
            {cancelError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{cancelError}</AlertDescription>
              </Alert>
            )}
            <div>
              <Label htmlFor="cancelReason">Motivo de cancelación (opcional)</Label>
              <Textarea
                id="cancelReason"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Describe el motivo de la cancelación..."
              />
            </div>
            <div className="space-y-2">
              <Label>Confirmación</Label>
              <Input
                placeholder="Escribe CANCELAR"
                value={cancelConfirmText}
                onChange={(e) => setCancelConfirmText(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={closeCancelDialog} disabled={cancelling}>
                Volver
              </Button>
              <Button
                variant="destructive"
                onClick={handleCancelSubscription}
                disabled={cancelConfirmText !== 'CANCELAR' || cancelling}
              >
                {cancelling ? (
                  <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Cancelando...</>
                ) : (
                  'Confirmar Cancelación'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VirtualPosSubscriptionDetail;
